import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { isEmpty } from 'lodash'
import React from 'react'

import { apiEndpoints } from 'api'
import {
  batchTypesMap,
  commonAttributes,
  levelMap,
  statuses,
  tableColumnDefaultOption,
  yesNoOptions
} from 'app/config'
import { validateNotEmpty, validateCount, validateIntFee } from 'app/validators'

import { BatchActivitiesWidget } from './BatchActivitiesWidget'

dayjs.extend(customParseFormat)

// The shared level column labels options by name alone, which suits the table
// filter but would save "beginner" into a field that stores '1' / '2' / '3'.
// Editing needs the stored key as the value. `data` is either the
// EXCEL_COURSE_LEVELS config response (the options) or the batch's own stored
// level (what the form shows on open).
export const structureBatchLevels = (data) => {
  if (data?.results) {
    return Object.entries(data.results[0].value).map(([key, label]) => ({
      name: label,
      uuid: key
    }))
  }
  return [{ name: levelMap[data], uuid: data }]
}

export const columns = [
  {
    ...commonAttributes.uuid
  },
  {
    ...commonAttributes.teacher,
    name: 'teacher_name',
    label: 'teacher',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'teacher_name'
    }
  },
  {
    // All teachers mapped to this batch, treated as equal (no primary/secondary).
    // Form-only multi-select (hidden from the table — the column above already
    // shows the teacher name). Backed by Batch.teachers (M2M); the backend keeps
    // the legacy single `teacher` FK in sync as one of these.
    name: 'teachers',
    label: 'Teachers (all equal)',
    formDataKey: 'teachers',
    widget: 'autocomplete',
    multiple: true,
    optional: true,
    minCharactersToSearch: 0,
    getStructuredValues: (data) => {
      const getItemStructure = (item) =>
        Object.prototype.hasOwnProperty.call(item, 'user')
          ? {
              uuid: item.user.uuid,
              name: `${item.user.first_name} ${item.user.last_name}`
            }
          : { uuid: item.uuid, name: item.name }
      if (Array.isArray(data)) {
        return data.map(getItemStructure)
      } else if (
        data &&
        Object.prototype.hasOwnProperty.call(data, 'results')
      ) {
        return data.results.map(getItemStructure)
      } else if (data) {
        return [getItemStructure(data)]
      }
      return []
    },
    getFormDataValue: (data) => {
      if (!Array.isArray(data)) return []
      return data.map((item) =>
        Object.prototype.hasOwnProperty.call(item, 'user')
          ? item.user.uuid
          : item.uuid
      )
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'teachers',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      url: `${apiEndpoints.user}/teacher`
    },
    options: {
      ...tableColumnDefaultOption.options,
      display: false,
      disableFilter: true
    }
  },
  {
    name: 'course',
    label: 'Course',
    widget: 'autocomplete',
    minCharactersToSearch: 0,
    validate: validateNotEmpty,
    getStructuredValues: (data) => {
      const getItemStructure = (item) => {
        return {
          uuid: item.uuid,
          name: !isEmpty(item.name)
            ? item.name
            : `${item.subjects.map((b) => b.name).join(', ')} | ${
                item.subjects[0]?.subject_category?.type === 'academic'
                  ? `${item.standards
                      .map((b) => b.name)
                      .join(', ')} | ${item.boards
                      .map((b) => b.name)
                      .join(', ')}`
                  : `${item.min_age} - ${item.max_age}`
              }`
        }
      }

      if (Array.isArray(data)) {
        return data.map((item) => {
          return getItemStructure(item)
        })
      } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
        return data.results.map((item) => {
          return getItemStructure(item)
        })
      }
      return [getItemStructure(data)]
    },
    getFormDataValue: (data) => {
      const getItemStructure = (item) => {
        return item.uuid
      }
      return getItemStructure(data)
    },
    getOptionLabelKey: 'name',
    getOptionSelectedKey: 'uuid',
    resultStructure: {
      keyName: 'course',
      keyValue: 'uuid'
    },
    apiOptions: {
      method: 'get',
      url: apiEndpoints.course
    },
    options: {
      display: 'false'
    },
    editable: false
  },
  {
    name: 'course.subjects',
    label: 'Course Subject',
    disabledInForm: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'subjects',
      customBodyRender: (value) => (
        <span>
          {Array.isArray(value) && value.map((item) => item.name).join(', ')}
        </span>
      )
    }
  },
  {
    ...commonAttributes.standard,
    name: 'course.standards',
    label: 'Course Standard',
    disabledInForm: true,
    apiOptions: {
      method: 'get',
      url: `${apiEndpoints.standard}?include_is_range=true`
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'standards',
      customBodyRender: (value) => (
        <span>
          {Array.isArray(value) && value.map((item) => item.name).join(', ')}
        </span>
      )
    }
  },
  {
    editable: false,
    ...commonAttributes.centre
  },
  {
    name: 'batch_activities',
    label: '',
    viewOnlyInModal: true,
    modalEdit: true,
    widget: BatchActivitiesWidget,
    options: {
      ...tableColumnDefaultOption.options,
      display: false,
      disableFilter: true
    }
  },
  {
    name: 'type',
    label: 'Batch Type',
    widget: 'select',
    widgetFilter: true,
    validate: validateNotEmpty,
    valueLabelMappings: batchTypesMap,
    isValueKey: true,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'type',
      customBodyRender: (value) => {
        return <div>{value}</div>
      }
    },
    editable: false
  },
  {
    ...commonAttributes.level,
    getStructuredValues: structureBatchLevels
  },
  {
    name: 'status',
    label: 'Batch Status',
    widget: 'select',
    widgetFilter: true,
    disabledInForm: true,
    validate: validateNotEmpty,
    valueLabelMappings: statuses,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'status'
    }
  },
  {
    name: 'is_play_care',
    label: 'Is drop-in care?',
    widget: 'select',
    isValueKey: true,
    widgetFilter: true,
    disabledInForm: true,
    validate: validateNotEmpty,
    valueLabelMappings: yesNoOptions,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'is_play_care'
    }
  },
  {
    name: 'enrolment_count',
    label: 'Enrolment Count',
    validate: validateCount,
    editable: false,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true
    }
  },
  {
    name: 'enrolment_capacity',
    label: 'Enrolment Capacity',
    validate: validateCount,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true
    }
  },
  {
    name: 'coins_per_session',
    label: 'Coins per session',
    validate: validateCount,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true
    }
  },
  {
    name: 'coins_per_month',
    label: 'Coins per month',
    validate: validateCount,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true
    }
  },
  {
    name: 'fee',
    label: 'Batch fees',
    validate: validateIntFee,
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'fee'
    }
  },
  {
    name: 'single_session_capacity',
    label: 'Extra Capacity',
    validate: validateCount,
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true
    }
  }
]
